/**
 * Vue Router guard factory: `requireCapability(key)`.
 *
 * Part of the frontend-rearchitecture (PR-7). Pairs the route layer with the
 * backend `capability_required(...)` decorator (PR-3), the login response
 * `capabilities: string[]` field (PR-4), the Distributor capability columns
 * (PR-5), and the Pinia `useAccountStore` + `usePermissions()` composable
 * (PR-6).
 *
 * ACL-PR-13f: this is the sole router guard factory for capability gates.
 * The legacy role-based guard factory was deleted in PR-13f after every
 * route in `router/index.js` was migrated onto `requireCapability`.
 *
 * T3.3 audit (2026-05-14): the deleted `requireRole(...)` factory had a
 * `localStorage.clear()` branch that fired when an authenticated Shopper
 * tried to enter an admin route — force-logout UX, not a security gate
 * (the Shopper's token only carries Shopper caps; backend
 * `capability_required` would deny the API call regardless). The
 * deletion has no security implications. The UX trade-off is minor:
 * post-PR-13f a Shopper denied an admin route is redirected to `/login`
 * but still holds their Shopper session in localStorage; navigating
 * back to a Shopper route works without re-login. If a future review
 * decides forced re-login on cross-tier denial is desired UX, port the
 * `localStorage.clear()` here (after a `useAccountStore.$reset()` /
 * `store.dispatch('logout')` call so reactive state is also cleared).
 *
 * Usage:
 *
 *     import { requireCapability } from '@/router/requireCapability'
 *
 *     {
 *         path: '/stores/add',
 *         component: CampaignAddEdit,
 *         beforeEnter: requireCapability('distributor.stores.edit'),
 *     }
 *
 * Behaviour:
 *   - Reads `capabilities` from the Pinia `useAccountStore`.
 *   - If the store's capabilities array includes the requested `key`, calls
 *     `next()` (allow).
 *   - Otherwise redirects to `redirectTo` (default `'/login'`).
 *
 * The guard is a plain function (not reactive). Vue Router invokes it on
 * every navigation, which re-reads the current store state each time —
 * reactivity inside the guard is therefore unnecessary.
 */
import { useAccountStore } from '@/stores/account'

/**
 * Build a Vue Router `beforeEnter` guard that allows navigation only if the
 * authenticated account has the given capability key.
 *
 * @param {string} key  Capability key (e.g. `'campaign.create'`). Must match
 *                      a key defined in the backend capability registry
 *                      (`api/src/models/capabilities.py`). A non-string key
 *                      (null/undefined/object) fails closed — the guard will
 *                      always redirect, since `[].indexOf(undefined) === -1`.
 * @param {string} [redirectTo='/login']  Path to redirect to when the
 *                      capability is missing. Defaults to `/login` for the
 *                      common unauthenticated case. Admin-area routes
 *                      typically pass `'/admin'` here to preserve UX for
 *                      authenticated-but-unprivileged users.
 * @returns {(to: object, from: object, next: Function) => void}
 *          A Vue Router navigation guard.
 */
export function requireCapability(key, redirectTo = '/login') {
    return (to, from, next) => {
        const account = useAccountStore()
        const capabilities = account.capabilities
        if (Array.isArray(capabilities) && capabilities.includes(key)) {
            return next()
        }
        return next(redirectTo)
    }
}
